import { Fragment, useEffect, useMemo, useState } from 'react';

import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SearchIcon from '@mui/icons-material/Search';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import dayjs from 'dayjs';

import { ClientDropdown, DateRangePicker } from '@src/components';
import { fetchTaskLog, listTaskLogs } from '@src/api/taskLogger';
import { formatDateToYMD } from '@src/utils/dateUtils';
import { useMessage } from '@src/context/common';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: 'running', label: 'Running' },
  { value: 'succeeded', label: 'Succeeded' },
  { value: 'failed', label: 'Failed' },
];

const statusColorMap = {
  running: 'info',
  succeeded: 'success',
  failed: 'error',
};

const pageSize = 100;

const getDefaultDates = () => {
  const end = new Date();
  const start = new Date();
  start.setDate(end.getDate() - 6);
  return [start, end];
};

function formatDateTime(value) {
  if (!value) return '—';
  const parsed = dayjs(value);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD HH:mm') : '—';
}

export default function TaskLoggerPage() {
  const [defaultStart, defaultEnd] = useMemo(getDefaultDates, []);
  const message = useMessage();
  const [rows, setRows] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [detailLoading, setDetailLoading] = useState({});
  const [expandedRowId, setExpandedRowId] = useState(null);
  const [detailMap, setDetailMap] = useState({});
  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [filters, setFilters] = useState({
    client: '',
    status: '',
    search: '',
    created_after: formatDateToYMD(defaultStart),
    created_before: formatDateToYMD(defaultEnd),
  });

  const fetchLogs = async (pageParam = page, activeFilters = filters) => {
    setLoading(true);
    const cleanSearch = activeFilters.search?.trim?.() ?? '';
    try {
      const params = {
        page: pageParam,
        page_size: pageSize,
        client: activeFilters.client || undefined,
        status: activeFilters.status || undefined,
        search: cleanSearch || undefined,
        created_after: activeFilters.created_after || undefined,
        created_before: activeFilters.created_before || undefined,
        ordering: '-created',
      };
      const res = await listTaskLogs(params);
      const { results = [], count = 0 } = res?.data || {};
      setRows(results);
      setTotalCount(count);
      setTotalPages(Math.max(1, Math.ceil(count / pageSize)));
    } catch (error) {
      console.error('Error fetching task logs', error);
      message?.error?.('Failed to load task logs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs(page);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  // Live-refresh any in-flight runs so you can watch a task process without
  // hitting Refresh. Polls only the running rows (and pulls fresh messages for
  // an expanded running row); stops as soon as nothing is running.
  const runningIds = useMemo(
    () => rows.filter((row) => row.status === 'running').map((row) => row.id),
    [rows],
  );
  const runningKey = runningIds.join(',');

  useEffect(() => {
    if (!runningIds.length) return undefined;
    const intervalId = setInterval(() => {
      Promise.all(
        runningIds.map((id) =>
          fetchTaskLog(id, { includeMessages: expandedRowId === id })
            .then((res) => res?.data)
            .catch(() => null),
        ),
      ).then((responses) => {
        const byId = new Map(responses.filter(Boolean).map((data) => [data.id, data]));
        if (!byId.size) return;
        setRows((prev) =>
          prev.map((row) => {
            const updated = byId.get(row.id);
            if (!updated) return row;
            return {
              ...row,
              status: updated.status,
              finished_at: updated.finished_at,
              duration_seconds: updated.duration_seconds,
              message_count: updated.message_count ?? row.message_count,
            };
          }),
        );
        const expandedDetail = expandedRowId != null ? byId.get(expandedRowId) : null;
        if (expandedDetail?.messages) {
          setDetailMap((prev) => ({ ...prev, [expandedRowId]: expandedDetail }));
        }
      });
    }, 4000);
    return () => clearInterval(intervalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runningKey, expandedRowId]);

  const updateFiltersAndFetch = (nextFilters) => {
    setFilters(nextFilters);
    setPage(1);
    fetchLogs(1, nextFilters);
  };

  const handleToggleRow = async (id) => {
    if (expandedRowId === id) {
      setExpandedRowId(null);
      return;
    }

    setExpandedRowId(id);
    if (!detailMap[id]) {
      setDetailLoading((prev) => ({ ...prev, [id]: true }));
      try {
        const res = await fetchTaskLog(id, { includeMessages: true });
        if (res?.data) {
          setDetailMap((prev) => ({ ...prev, [id]: res.data }));
        }
      } catch (error) {
        console.error('Failed to load task details', error);
        message?.error?.('Could not load task details');
      } finally {
        setDetailLoading((prev) => ({ ...prev, [id]: false }));
      }
    }
  };

  const handleDateRangeChange = ({ selection }) => {
    const { startDate: nextStart, endDate: nextEnd } = selection;
    setStartDate(nextStart);
    setEndDate(nextEnd);
    updateFiltersAndFetch({
      ...filters,
      created_after: formatDateToYMD(nextStart),
      created_before: formatDateToYMD(nextEnd),
    });
  };

  const handleSearch = () => {
    updateFiltersAndFetch({
      ...filters,
      search: filters.search?.trim?.() ?? '',
    });
  };

  const handleReset = () => {
    const [nextStart, nextEnd] = getDefaultDates();
    setStartDate(nextStart);
    setEndDate(nextEnd);
    updateFiltersAndFetch({
      client: '',
      status: '',
      search: '',
      created_after: formatDateToYMD(nextStart),
      created_before: formatDateToYMD(nextEnd),
    });
  };

  const renderStatusChip = (status) => (
    <Chip
      label={status ? status.replace(/_/g, ' ') : 'Unknown'}
      color={statusColorMap[status] || 'default'}
      size="small"
      sx={{ textTransform: 'capitalize' }}
    />
  );

  return (
    <Box sx={{ flex: '1 1 auto', p: 2 }}>
      <Stack spacing={2}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Box>
            <Typography variant="h5" fontWeight={700}>
              Task Logger
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View recent background tasks with client, status, and date filters.
            </Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <Button variant="outlined" startIcon={<RefreshIcon />} onClick={() => fetchLogs(page)}>
              Refresh
            </Button>
            <Button variant="text" onClick={handleReset}>
              Reset
            </Button>
          </Stack>
        </Stack>

        <Card>
          <CardHeader
            title="Filters"
            subheader="Filter by client, name, status, and created date range."
            action={
              <Button variant="contained" startIcon={<SearchIcon />} onClick={handleSearch}>
                Apply filters
              </Button>
            }
          />
          <Divider />
          <CardContent>
            <Stack spacing={2}>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems="flex-start">
                <Box sx={{ minWidth: { xs: '100%', md: 260 } }}>
                  <ClientDropdown
                    value={filters.client}
                    onChange={(value) => updateFiltersAndFetch({ ...filters, client: value || '' })}
                  />
                </Box>
                <FormControl sx={{ minWidth: { xs: '100%', md: 200 } }} size="small">
                  <InputLabel>Status</InputLabel>
                  <Select
                    label="Status"
                    value={filters.status}
                    onChange={(e) => updateFiltersAndFetch({ ...filters, status: e.target.value })}
                  >
                    {STATUS_OPTIONS.map((option) => (
                      <MenuItem key={option.value || 'all'} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box sx={{ minWidth: { xs: '100%', md: 280 } }}>
                  <DateRangePicker startDate={startDate} endDate={endDate} onChange={handleDateRangeChange} />
                </Box>
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={2}>
                <TextField
                  label="Task name"
                  placeholder="Search by task name"
                  value={filters.search}
                  onChange={(e) => setFilters((prev) => ({ ...prev, search: e.target.value }))}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSearch();
                  }}
                  size="small"
                  sx={{ minWidth: { xs: '100%', md: 320 } }}
                />
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        <Card>
          <CardHeader title="Recent task runs" subheader={`${totalCount.toLocaleString()} tasks`} />
          <Divider />
          <CardContent>
            <Typography variant="body2" color="text.secondary" gutterBottom>
              Click a row to expand messages (most recent first). Opening one row will close any other open row.
            </Typography>
            <TableContainer component={Paper}>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell width={48} />
                    <TableCell>Name</TableCell>
                    <TableCell>Client / Parent</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Messages</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell>Finished</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loading ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Stack spacing={1} alignItems="center" py={4}>
                          <CircularProgress size={24} />
                          <Typography variant="body2" color="text.secondary">
                            Loading task logs...
                          </Typography>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ) : rows.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={7} align="center">
                        <Typography variant="body2" color="text.secondary" py={3}>
                          No task logs found for the selected filters.
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((row) => {
                      const isExpanded = expandedRowId === row.id;
                      const detail = detailMap[row.id];
                      const isDetailLoading = detailLoading[row.id];
                      const messages = detail?.messages || [];
                      return (
                        <Fragment key={row.id}>
                          <TableRow hover>
                            <TableCell>
                              <IconButton size="small" onClick={() => handleToggleRow(row.id)}>
                                {isExpanded ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}
                              </IconButton>
                            </TableCell>
                            <TableCell sx={{ maxWidth: 320, whiteSpace: 'nowrap', textOverflow: 'ellipsis', overflow: 'hidden' }}>
                              <Typography variant="subtitle2">{row.name || 'Untitled task'}</Typography>
                              <Typography variant="caption" color="text.secondary">
                                #{row.id}
                              </Typography>
                            </TableCell>
                            <TableCell sx={{ minWidth: 180 }}>
                              {row.client_name ? (
                                row.client_name
                              ) : row.parent_company_name ? (
                                <Stack direction="row" spacing={0.5} alignItems="center">
                                  <span>{row.parent_company_name}</span>
                                  <Chip label="parent" size="small" variant="outlined" />
                                </Stack>
                              ) : (
                                row.client || '—'
                              )}
                            </TableCell>
                            <TableCell>{renderStatusChip(row.status)}</TableCell>
                            <TableCell>{row.message_count ?? 0}</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(row.created)}</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatDateTime(row.finished_at)}</TableCell>
                          </TableRow>
                          {isExpanded && (
                            <TableRow>
                              <TableCell colSpan={7} sx={{ bgcolor: (theme) => theme.palette.background.level1 ?? theme.palette.action.hover }}>
                                <Box
                                  sx={(theme) => ({
                                    border: `1px solid ${theme.palette.divider}`,
                                    borderRadius: 1,
                                    p: 2,
                                    bgcolor: theme.palette.background.paper,
                                  })}
                                >
                                  <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                                    <Typography variant="subtitle2">Details for #{row.id}</Typography>
                                    <Typography variant="caption" color="text.secondary">
                                      {row.name || 'Untitled task'}
                                    </Typography>
                                  </Stack>
                                  {isDetailLoading ? (
                                    <Stack spacing={1} alignItems="center" py={2}>
                                      <CircularProgress size={20} />
                                      <Typography variant="caption" color="text.secondary">
                                        Loading details...
                                      </Typography>
                                    </Stack>
                                  ) : messages.length === 0 ? (
                                    <Typography variant="body2" color="text.secondary">
                                      No log messages recorded for this task.
                                    </Typography>
                                  ) : (
                                    <Stack spacing={1}>
                                      {messages
                                        .slice()
                                        .sort((a, b) => dayjs(b.created).valueOf() - dayjs(a.created).valueOf())
                                        .map((msg) => (
                                          <Box
                                            key={msg.id}
                                            sx={(theme) => ({
                                              border: `1px solid ${theme.palette.divider}`,
                                              borderRadius: 1,
                                              p: 1.5,
                                              bgcolor: theme.palette.background.paper,
                                            })}
                                          >
                                            <Typography variant="caption" color="text.secondary">
                                              {formatDateTime(msg.created)}
                                            </Typography>
                                            <Typography variant="body2">{msg.message}</Typography>
                                          </Box>
                                        ))}
                                    </Stack>
                                  )}
                                </Box>
                              </TableCell>
                            </TableRow>
                          )}
                        </Fragment>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center" justifyContent="space-between" mt={3}>
              <Typography variant="body2" color="text.secondary">
                Showing {rows.length} of {totalCount.toLocaleString()} tasks (page size {pageSize})
              </Typography>
              <Pagination
                count={totalPages}
                page={page}
                onChange={(_, value) => setPage(value)}
                color="primary"
                shape="rounded"
                showFirstButton
                showLastButton
              />
            </Stack>
          </CardContent>
        </Card>
      </Stack>
    </Box>
  );
}
